import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import '../module_mechanics/styles/FormList.css';

import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner, faCalculator, 
    faFileInvoice, faMoneyBill, faBalanceScale, faBoxes,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEyeSlash,
    faShoppingCart, faEye, faPrint, faDollarSign, faCreditCard, faWallet,
    faReceipt, faHistory, faInfoCircle, faBox, faBarcode, faBuilding, 
    faLayerGroup, faCubes
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ==================== HELPER FUNCTIONS ====================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    return 0;
};

const adjustColor = (color, amount) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) return '#6366f1';
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
};

const getPaymentStatusText = (paid, grandTotal) => {
    const paidAmount = parseFloat(paid) || 0;
    const totalAmount = parseFloat(grandTotal) || 0;
    const balance = totalAmount - paidAmount;
    if (balance <= 0) return 'PAID';
    if (paidAmount === 0) return 'UNPAID';
    return 'PARTIAL';
};

// ==================== TRANSLATOR ====================
const createPurchasesListTranslator = () => {
    let cache = new Map();
    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };
    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const defaultTexts = {
        purchaseList: 'Bill-wise Products',
        formDetails: 'All purchase details are below',
        purchases: 'Purchases',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search purchases...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your purchase data...',
        selectShop: 'Please select a shop to view purchases',
        billNumber: 'Bill Number',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        date: 'Date',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        actions: 'Actions',
        addedBy: 'Added By',
        edit: 'Edit',
        delete: 'Delete',
        view: 'View',
        print: 'Print',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        billNumberFilter: 'Bill Number',
        supplierNameFilter: 'Supplier Name',
        phoneFilter: 'Phone Number',
        addedByFilter: 'Added By',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        paymentStatusFilter: 'Payment Status',
        filterBy: 'Filter By',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        purchaseDetails: 'Purchase Details',
        editPurchase: 'Edit Purchase',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this purchase?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        purchaseReport: 'Purchase Report',
        purchaseDirectory: 'Purchase Directory',
        totalPurchases: 'Total Purchases',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',
        items: 'Items',
        productName: 'Product',
        quantity: 'Qty',
        price: 'Price',
        total: 'Total',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        updatePurchase: 'Update Purchase',
        updating: 'Updating...',
        purchaseUpdated: 'Purchase updated successfully',
        updateError: 'Error updating purchase',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        supplierAddress: 'Address',
        notes: 'Notes',
        purchaseId: 'Purchase ID',
        itemBatch: 'Batch',
        itemUnit: 'Unit',
        srNo: 'Sr#',
        batch: 'Batch',
        barcode: 'Barcode',
        costPrice: 'Cost Price',
        sellPrice: 'Sell Price',
        stock: 'Stock',
        expiry: 'Expiry',
        category: 'Category',
        purchaseReceipt: 'Purchase Receipt',
        time: 'Time',
        supplier: 'Supplier',
        address: 'Address',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        noDataToExport: 'No data to export',
        reportGenerated: 'Report generated!',
        pdfGenerationError: 'Error generating PDF',
        readonlyNotice: 'items cannot be edited after creation',
        paymentStatus: 'Payment Status',
        receipt: 'Receipt'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        return defaultTexts;
    };

    loadFromCache();
    return { getTranslations };
};
const purchasesListTranslator = createPurchasesListTranslator();

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== SHOP SELECTOR MODAL ====================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading, darkMode }) => {
    if (!isOpen) return null;

    return (
        <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==================== CUSTOM DROPDOWN ====================
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: label } });
            window.dispatchEvent(event);
        }
    }, [isOpen, label]);

    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== label) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [label]);

    const filteredOptions = isHovering
        ? options
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div
            className="ozlistui-custom-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ozlistui-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==================== WIDTH MODES ====================
const widthModes = {
    narrow: { width: 400, label: 'Narrow', icon: faMinus },
    medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 850, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

// ==================== MAIN COMPONENT ====================
const CosmeticsInvoicePurchaseList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [filteredPurchases, setFilteredPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [purchaseToDelete, setPurchaseToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: null,
        date: '',
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        grand_total: '',
        paid: '',
        balance: '',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        bussinesslogo_url: '',
        businessaddress: '',
        fullname: '',
        email: '',
        phonenumber: '',
        gst_number: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);

    // Filter states
    const [filterBy, setFilterBy] = useState('');
    const [billNumberFilter, setBillNumberFilter] = useState('');
    const [supplierNameFilter, setSupplierNameFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const paymentStatusOptions = useMemo(() => [
        { value: '', label: translations.all || 'All' },
        { value: 'paid', label: translations.paid || 'Paid' },
        { value: 'unpaid', label: translations.unpaid || 'Unpaid' },
        { value: 'partial', label: translations.partiallyPaid || 'Partially Paid' }
    ], [translations]);

    const filterByOptions = useMemo(() => [
        { value: 'all', label: translations.all || 'All' },
        { value: 'billNumber', label: translations.billNumberFilter || 'Bill Number' },
        { value: 'supplierName', label: translations.supplierNameFilter || 'Supplier Name' },
        { value: 'phone', label: translations.phoneFilter || 'Phone Number' },
        { value: 'added_by', label: translations.addedByFilter || 'Added By' },
        { value: 'date', label: translations.dateRange || 'Date Range' },
        { value: 'paymentStatus', label: translations.paymentStatusFilter || 'Payment Status' }
    ], [translations]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    const pdfPageRangeOptions = useMemo(() => [
        { value: 'all', label: 'All Pages' },
        { value: 'range', label: 'Page Range' }
    ], []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============ THEME SYNC ============
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozlistui-primary', color);
        document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
        document.documentElement.style.setProperty('--ozlistui-primary-light', color);
        setCurrentTheme(color);

        let style = document.getElementById('dynamic-list-styles-purchase');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-list-styles-purchase';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozlistui-table thead th { color: ${color}; border-bottom-color: ${color}; }
            .ozlistui-payment-badge { border-color: ${color}40; }
            .ozlistui-page-number.ozlistui-active { background: ${color}; border-color: ${color}; }
            .ozlistui-action-btn.ozlistui-edit-btn:hover:not(:disabled) { background: ${color}; }
            .ozlistui-header-shop-btn, .ozlistui-header-filter-btn { background: linear-gradient(135deg, ${color}, ${adjustColor(color, -20)}); }
            .ozlistui-btn-primary { background: ${color}; }
            .ozlistui-custom-dropdown-option.ozlistui-selected { background: ${color}; }
            
            .ozlistui-purchase-group { margin-bottom: 0; border-radius: 0; overflow: visible; border: 0; }
            .ozlistui-dark .ozlistui-purchase-group { border-color: #374151; }
            .ozlistui-purchase-header { background: #f3f4f6; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; flex-wrap: wrap; gap: 8px; }
            .ozlistui-dark .ozlistui-purchase-header { background: #1f2937; border-bottom-color: #374151; }
            .ozlistui-purchase-header:hover { background: #e5e7eb; }
            .ozlistui-dark .ozlistui-purchase-header:hover { background: #2d3748; }
            .ozlistui-purchase-header-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
            .ozlistui-purchase-header-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
            .ozlistui-purchase-bill { font-weight: bold; font-family: monospace; font-size: 14px; color: #1f2937; }
            .ozlistui-dark .ozlistui-purchase-bill { color: #f3f4f6; }
            .ozlistui-purchase-supplier { font-size: 13px; color: #4b5563; }
            .ozlistui-dark .ozlistui-purchase-supplier { color: #9ca3af; }
            .ozlistui-purchase-date { font-size: 12px; color: #6b7280; }
            .ozlistui-dark .ozlistui-purchase-date { color: #9ca3af; }
            .ozlistui-purchase-amounts { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
            .ozlistui-purchase-total { font-weight: bold; color: #059669; }
            .ozlistui-purchase-paid { font-weight: bold; color: #3b82f6; }
            .ozlistui-purchase-balance.positive { color: #d97706; font-weight: bold; }
            .ozlistui-purchase-balance.zero { color: #059669; font-weight: bold; }
            .ozlistui-purchase-status-badge { padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
            .ozlistui-purchase-status-badge.paid { background: #10b98120; color: #10b981; border: 1px solid #10b98140; }
            .ozlistui-purchase-status-badge.unpaid { background: #ef444420; color: #ef4444; border: 1px solid #ef444440; }
            .ozlistui-purchase-status-badge.partial { background: #f59e0b20; color: #f59e0b; border: 1px solid #f59e0b40; }
            .ozlistui-purchase-actions { display: flex; gap: 6px; }
            .ozlistui-purchase-actions button { background: transparent; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; color: #6b7280; transition: all 0.2s; font-size: 13px; }
            .ozlistui-purchase-actions button:hover { background: #f3f4f6; color: #1f2937; }
            .ozlistui-dark .ozlistui-purchase-actions button:hover { background: #374151; color: #f3f4f6; }
            .ozlistui-purchase-actions .ozlistui-edit-action:hover { color: ${color}; }
            .ozlistui-purchase-actions .ozlistui-delete-action:hover { color: #ef4444; }
            .ozlistui-purchase-actions .ozlistui-print-action:hover { color: #059669; }
            .ozlistui-purchase-actions .ozlistui-view-action:hover { color: #3b82f6; }
            
            .ozlistui-products-table-wrap { overflow-x: auto; padding: 0; width: 100%; }
            .ozlistui-products-table { width: 100%; border-collapse: collapse; font-size: 13px; }
            .ozlistui-products-table thead th { background: transparent; color: #6b7280; font-weight: 600; padding: 8px 10px; text-align: left; border-bottom: 2px solid #e5e7eb; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
            .ozlistui-dark .ozlistui-products-table thead th { color: #9ca3af; border-bottom-color: #374151; }
            .ozlistui-products-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; color: #1f2937; vertical-align: middle; }
            .ozlistui-dark .ozlistui-products-table tbody td { border-bottom-color: #1f2937; color: #f3f4f6; }
            .ozlistui-products-table tbody tr:last-child td { border-bottom: none; }
            .ozlistui-products-table .ozlistui-text-right { text-align: right; }
            .ozlistui-products-table .ozlistui-text-center { text-align: center; }
            .ozlistui-products-table .ozlistui-product-name { font-weight: 600; }
            .ozlistui-products-table .ozlistui-total-cell { font-weight: bold; color: #059669; }
            .ozlistui-products-table .ozlistui-barcode-cell { font-family: monospace; font-size: 12px; color: #6b7280; }
        `;
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentTheme]);

    // ============ DARK MODE SYNC ============
    const syncDarkMode = useCallback(() => {
        const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
        setDarkMode(isDark);

        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isDark) {
                container.classList.add('ozlistui-dark');
            } else {
                container.classList.remove('ozlistui-dark');
            }
        }

        const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
        overlays.forEach(overlay => {
            if (isDark) {
                overlay.classList.add('ozlistui-dark');
            } else {
                overlay.classList.remove('ozlistui-dark');
            }
        });

        if (isDark) {
            document.body.classList.add('ozlistui-dark');
            document.documentElement.classList.add('ozlistui-dark');
        } else {
            document.body.classList.remove('ozlistui-dark');
            document.documentElement.classList.remove('ozlistui-dark');
        }
    }, []);

    useEffect(() => {
        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
                overlays.forEach(overlay => {
                    if (isDark) {
                        overlay.classList.add('ozlistui-dark');
                    } else {
                        overlay.classList.remove('ozlistui-dark');
                    }
                });
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [syncDarkMode, darkMode]);

    // ============ LANGUAGE SYNC ============
    const loadTranslations = useCallback(async (lang) => {
        const trans = await purchasesListTranslator.getTranslations(lang);
        setTranslations(trans);
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleStorageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage, loadTranslations]);

    // ============ RTL DIRECTION SYNC ============
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozlistui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ============ MODAL FUNCTIONS ============
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // ============ FETCH PURCHASES ============
    const fetchPurchases = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setPurchases(data.data || []);
                    setFilteredPurchases(data.data || []);
                    resetFilters();
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching purchases:', error);
            if (isMountedRef.current) showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, translations, showToast]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo_url || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || '',
                        phonenumber: data.phonenumber || '',
                        gst_number: data.gst_number || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || '',
                        bussinesslogo: user.bussinesslogo || '',
                        bussinesslogo_url: user.bussinesslogo_url || '',
                        businessaddress: user.business_address || '',
                        fullname: user.fullname || '',
                        email: user.email || '',
                        phonenumber: user.phonenumber || '',
                        gst_number: user.gst_number || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: user.bussinesslogo || '',
                    bussinesslogo_url: user.bussinesslogo_url || '',
                    businessaddress: user.business_address || '',
                    fullname: user.fullname || '',
                    email: user.email || '',
                    phonenumber: user.phonenumber || '',
                    gst_number: user.gst_number || ''
                });
            }
        }
    }, [user]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchPurchases();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
                fetchPurchases();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, fetchPurchases]);

    // ============ FILTERS ============
    const applyFilters = useCallback(() => {
        let filtered = [...purchases];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.bill_no?.toLowerCase().includes(term) ||
                purchase.supplier_name?.toLowerCase().includes(term) ||
                purchase.phone?.toLowerCase().includes(term) ||
                purchase.username?.toLowerCase().includes(term)
            );
        }
        if (filterBy === 'billNumber' && billNumberFilter.trim()) {
            const term = billNumberFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.bill_no?.toLowerCase().includes(term));
        }
        if (filterBy === 'supplierName' && supplierNameFilter.trim()) {
            const term = supplierNameFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.supplier_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.phone?.toLowerCase().includes(term));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.username?.toLowerCase().includes(term));
        }
        if (filterBy === 'paymentStatus' && paymentStatusFilter) {
            if (paymentStatusFilter === 'paid') {
                filtered = filtered.filter(purchase => {
                    const balance = parseFloat(purchase.balance) || 0;
                    return balance <= 0;
                });
            } else if (paymentStatusFilter === 'unpaid') {
                filtered = filtered.filter(purchase => {
                    const paid = parseFloat(purchase.paid) || 0;
                    return paid === 0;
                });
            } else if (paymentStatusFilter === 'partial') {
                filtered = filtered.filter(purchase => {
                    const paid = parseFloat(purchase.paid) || 0;
                    const balance = parseFloat(purchase.balance) || 0;
                    return paid > 0 && balance > 0;
                });
            }
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                purchaseDate.setHours(0, 0, 0, 0);
                return purchaseDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                return purchaseDate <= end;
            });
        }
        setFilteredPurchases(filtered);
        setCurrentPage(1);
    }, [purchases, searchTerm, filterBy, billNumberFilter, supplierNameFilter, phoneFilter, addedByFilter, paymentStatusFilter, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    const resetFilters = useCallback(() => {
        setFilterBy('');
        setBillNumberFilter('');
        setSupplierNameFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setPaymentStatusFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    // ============ UTILITY FUNCTIONS ============
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;

        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') {
            return 15;
        }
        return 10;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPurchases = filteredPurchases.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentPurchases };
    }, [filteredPurchases, currentPage, itemsPerPage]);

    // ============ FIXED PDF EXPORT ============
    // ============ OPTIMIZED FAST PDF EXPORT ============
const exportToPDF = useCallback(async () => {
    if (!filteredPurchases || filteredPurchases.length === 0) {
        showToast('warning', translations.noDataToExport || 'No data to export');
        return;
    }
    setPdfLoading(true);
    try {
        const currentLang = currentLanguage;
        const pdfThemeColor = currentTheme;

        const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
        const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
        
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        
        const pdf = new jsPDF({ 
            orientation: pdfOrientation, 
            unit: 'mm', 
            format: pageFormat,
            compress: true 
        });
        
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const tableWidth = pageWidth - (margin * 2);
        let currentY = margin;
        let pageNum = 1;
        const totalPages = Math.ceil(filteredPurchases.length / 15);

        const formatCurrencyForPDF = (amount) => {
            if (!amount && amount !== 0) return 'Rs. 0.00';
            return `Rs. ${parseFloat(amount).toFixed(2)}`;
        };

        const formatDateForPDF = (dateString) => {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            } catch { return dateString; }
        };

        const getPDFTranslation = (key, fallback = '') => {
            return translations[key] || fallback;
        };

        // ============ HEADER - ONLY ON FIRST PAGE ============
        const addMainHeader = (pdf) => {
            const y = margin;
            
            pdf.setFontSize(8);
            pdf.setTextColor('#6b7280');
            pdf.setFont('helvetica', 'normal');
            pdf.text('PURCHASE REPORT', pageWidth / 2, y, { align: 'center' });
            
            pdf.setFontSize(20);
            pdf.setTextColor('#1f2937');
            pdf.setFont('helvetica', 'bold');
            const businessName = businessInfo?.businessname || currentShop?.shop_name || 'WORLD BRAND COSMETICS';
            pdf.text(businessName.toUpperCase(), pageWidth / 2, y + 8, { align: 'center' });
            
            pdf.setDrawColor(pdfThemeColor);
            pdf.setLineWidth(0.5);
            pdf.line(pageWidth / 2 - 30, y + 12, pageWidth / 2 + 30, y + 12);
            
            pdf.setFontSize(8);
            pdf.setTextColor('#6b7280');
            pdf.setFont('helvetica', 'normal');
            const generatedDate = new Date().toLocaleString(currentLang);
            pdf.text(
                `Total Purchases: ${filteredPurchases.length} | Generated: ${generatedDate}`,
                pageWidth / 2,
                y + 18,
                { align: 'center' }
            );
            
            pdf.setDrawColor('#e5e7eb');
            pdf.setLineWidth(0.3);
            pdf.line(margin, y + 22, pageWidth - margin, y + 22);
            
            pdf.setFontSize(6);
            pdf.setTextColor('#6b7280');
            pdf.setFont('helvetica', 'normal');
            const reportId = `#${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}`;
            pdf.text(`${getPDFTranslation('reportId', 'Report ID')}: ${reportId}`, margin, y + 27);
            pdf.text(`${getPDFTranslation('generatedBy', 'Generated by')}: ${escapeHtml(user?.fullname || user?.username || 'System')}`, pageWidth / 2, y + 27, { align: 'center' });
            pdf.text(`${getPDFTranslation('shopCode', 'Shop Code')}: ${escapeHtml(currentShop?.shop_code || 'N/A')}`, pageWidth - margin, y + 27, { align: 'right' });
            
            return y + 32;
        };

        // ============ FOOTER - ON ALL PAGES ============
        const addFooter = (pdf, pageNum, totalPages) => {
            const y = pageHeight - 10;
            pdf.setFontSize(6);
            pdf.setTextColor('#9ca3af');
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, y, { align: 'center' });
            pdf.text(translations.poweredBy || 'Powered by OurZest', pageWidth / 2, y + 5, { align: 'center' });
        };

        // Add main header on first page
        currentY = addMainHeader(pdf);

        // Process each purchase
        let processedCount = 0;

        for (let idx = 0; idx < filteredPurchases.length; idx++) {
            const purchase = filteredPurchases[idx];
            
            if (idx > 0 && currentY > pageHeight - 40) {
                addFooter(pdf, pageNum, totalPages);
                pdf.addPage();
                pageNum++;
                currentY = margin;
            }

            const items = purchase.items || [];
            const hasItems = items.length > 0;

            // ============ BILL HEADER ROW - FULL WIDTH ============
            pdf.setFillColor('#f3f4f6');
            pdf.setDrawColor('#e5e7eb');
            pdf.rect(margin, currentY, tableWidth, 9, 'F');
            pdf.rect(margin, currentY, tableWidth, 9, 'S');

            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor('#1f2937');
            pdf.text(`#${escapeHtml(purchase.bill_no)}`, margin + 3, currentY + 4);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor('#4b5563');
            pdf.text(escapeHtml(purchase.supplier_name) || '-', margin + 50, currentY + 4);
            
            pdf.setFontSize(7);
            pdf.setTextColor('#6b7280');
            pdf.text(formatDateForPDF(purchase.date), margin + 120, currentY + 4);

            pdf.setTextColor('#059669');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            const totalX = margin + tableWidth - 3;
            pdf.text(formatCurrencyForPDF(purchase.grand_total), totalX - 100, currentY + 4);
            
            pdf.setTextColor('#3b82f6');
            pdf.text(formatCurrencyForPDF(purchase.paid), totalX - 60, currentY + 4);
            
            pdf.setTextColor(parseFloat(purchase.balance) > 0 ? '#d97706' : '#059669');
            pdf.text(formatCurrencyForPDF(purchase.balance), totalX - 20, currentY + 4);

            currentY += 11;

            // ============ PRODUCTS TABLE - FULL WIDTH WITH WIDER PRODUCT COLUMN ============
            if (hasItems) {
                const tableData = items.map((item, itemIdx) => {
                    const itemTotal = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
                    const stockPacks = parseFloat(item.available_stock_packs || item.quantity || 0);
                    const stockUnits = parseFloat(item.bquantity ? parseBQuantityNumeric(item.bquantity) : 0);
                    const isPackingMode = (item.purchase_type || 'packing') === 'packing';
                    const stockDisplay = isPackingMode ? stockPacks.toFixed(0) : stockUnits.toFixed(2);

                    return [
                        (itemIdx + 1).toString(),
                        item.batch_no || '-',
                        item.product_name || '-',
                        item.category || '-',
                        item.barcode || '-',
                        formatCurrencyForPDF(item.price),
                        item.sell_per_pack ? formatCurrencyForPDF(item.sell_per_pack) : '-',
                        parseFloat(item.quantity).toFixed(2),
                        stockDisplay,
                        formatCurrencyForPDF(itemTotal),
                        item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'
                    ];
                });

                autoTable(pdf, {
                    startY: currentY,
                    head: [[
                        '#', 'Batch', 'Product', 'Category', 'Barcode',
                        'Cost', 'Sell', 'Qty', 'Stock', 'Total', 'Expiry'
                    ]],
                    body: tableData,
                    theme: 'plain',
                    styles: {
                        fontSize: 7,
                        cellPadding: 2.5,
                        textColor: '#1f2937',
                        lineColor: '#e5e7eb',
                        lineWidth: 0.1,
                    },
                    headStyles: {
                        fillColor: '#f9fafb',
                        textColor: '#6b7280',
                        fontStyle: 'bold',
                        fontSize: 6.5,
                        halign: 'left',
                        cellPadding: 3,
                    },
                    columnStyles: {
                        0: { halign: 'center', cellWidth: 10 },      // # - smaller
                        1: { cellWidth: 25 },                         // Batch - smaller
                        2: { cellWidth: 65, fontStyle: 'bold' },     // Product - WIDER (was 38)
                        3: { cellWidth: 25 },                         // Category - smaller
                        4: { cellWidth: 35, fontStyle: 'italic' },   // Barcode - slightly smaller
                        5: { halign: 'right', cellWidth: 22 },       // Cost - smaller
                        6: { halign: 'right', cellWidth: 20 },       // Sell - smaller
                        7: { halign: 'right', cellWidth: 14 },       // Qty - smaller
                        8: { halign: 'right', cellWidth: 14 },       // Stock - smaller
                        9: { halign: 'right', cellWidth: 25, fontStyle: 'bold', textColor: '#059669' }, // Total
                        10: { cellWidth: 22 },                       // Expiry
                    },
                    margin: { left: margin, right: margin },
                    tableWidth: tableWidth,
                });

                const lastTableRow = pdf.lastAutoTable.finalY || currentY + 10;
                currentY = lastTableRow + 3;
            } else {
                pdf.setFillColor('#fafafa');
                pdf.rect(margin, currentY, tableWidth, 8, 'F');
                pdf.setDrawColor('#e5e7eb');
                pdf.rect(margin, currentY, tableWidth, 8, 'S');
                
                pdf.setFontSize(7);
                pdf.setTextColor('#6b7280');
                pdf.setFont('helvetica', 'italic');
                pdf.text('No products found', margin + 3, currentY + 5);
                currentY += 10;
            }

            currentY += 4;
            processedCount++;
        }

        addFooter(pdf, pageNum, totalPages);

        const fileName = `Purchase_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${new Date().toISOString().slice(0, 10)}.pdf`;
        pdf.save(fileName);
        
        showToast('success', `${translations.reportGenerated || 'Report generated!'} ${filteredPurchases.length} ${translations.purchases || 'purchases'} exported successfully!`);

    } catch (error) {
        console.error('PDF Error:', error);
        showToast('error', translations.pdfGenerationError || 'Error generating PDF: ' + error.message);
    } finally {
        setPdfLoading(false);
    }
}, [filteredPurchases, currentLanguage, pdfOrientation, pdfPageSize, translations, businessInfo, currentShop, user, showToast, currentTheme]);
    // ============ FIXED THERMAL PRINT ============
    const handlePrintBill = async (purchase) => {
        if (!purchase) return;

        showToast('info', 'Loading purchase details for print...');

        let purchaseData = purchase;

        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    purchaseData = data.data;
                } else {
                    showToast('error', 'Could not load purchase details for printing');
                    return;
                }
            } catch (error) {
                console.error('Error fetching purchase details for print:', error);
                showToast('error', 'Could not load purchase details for printing');
                return;
            }
        }

        if (!purchaseData.items || purchaseData.items.length === 0) {
            showToast('warning', 'No items to print receipt');
            return;
        }

        const printWindow = window.open('', '_blank', 'width=400,height=600');
        if (!printWindow) {
            showToast('error', 'Please allow popups for printing');
            return;
        }

        const shopName = businessInfo?.businessname || currentShop?.shop_name || 'OurZest Store';
        const shopAddress = businessInfo?.businessaddress || '';
        const businessPhone = businessInfo?.phonenumber || '';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const supplierName = purchaseData.supplier_name || 'Walk-in Supplier';

        const formatReceiptDateTime = (dateString) => {
            const date = dateString ? new Date(dateString) : new Date();
            const validDate = isNaN(date.getTime()) ? new Date() : date;
            const datePart = validDate.toLocaleDateString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
            const timePart = validDate.toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit', hour12: true
            });
            return `${datePart} ${timePart}`;
        };

        const formatReceiptCurrency = (amount) => {
            if (!amount && amount !== 0) return 'Rs. 0';
            return `Rs. ${parseFloat(amount).toFixed(0)}`;
        };

        const receiptDateTime = formatReceiptDateTime(purchaseData.date);
        const grandTotal = purchaseData.grand_total || '0.00';
        const paidAmount = purchaseData.paid || '0.00';
        const balance = purchaseData.balance || (parseFloat(grandTotal) - parseFloat(paidAmount)).toFixed(2);
        const tax = purchaseData.tax || 0;
        const discount = purchaseData.discount || 0;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Purchase Bill ${purchaseData.bill_no}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    @page { size: 80mm auto; margin: 0; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body {
                        background: #ffffff;
                        font-family: 'Courier New', Courier, monospace;
                        font-weight: 700;
                        color: #000000;
                        font-size: 12px;
                        line-height: 1.3;
                        padding: 5px 4px;
                        max-width: 80mm;
                        margin: 0 auto;
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        min-height: 100vh;
                    }
                    .receipt { width: 100%; padding: 2px 0; margin: 0 auto; }
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .text-left { text-align: left; }
                    .fw-900 { font-weight: 900; }
                    .fw-700 { font-weight: 700; }
                    .border-bottom { border-bottom: 3px double #000; padding-bottom: 4px; margin-bottom: 4px; }
                    .border-top { border-top: 2px solid #000; padding-top: 4px; margin-top: 4px; }
                    .border-dash { border-bottom: 1px dashed #000; padding-bottom: 3px; margin-bottom: 3px; }
                    .flex-row { display: flex; justify-content: space-between; align-items: baseline; }
                    .header-center { text-align: center; border-bottom: 3px double #000; padding-bottom: 4px; margin-bottom: 4px; }
                    .header-logo { display: block; margin: 0 auto 2px auto; max-height: 85px; max-width: 120px; width: auto; height: auto; object-fit: contain; }
                    .business-name { font-size: 17px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; }
                    .business-phone { font-size: 11px; font-weight: 700; margin-top: 1px; color: #000; }
                    .business-addr { font-size: 10px; font-weight: 700; margin-top: 1px; }
                    .receipt-title { text-align: center; font-size: 18px; font-weight: 900; letter-spacing: 2px; border-bottom: 2px solid #000; padding-bottom: 2px; margin-bottom: 2px; }
                    .receipt-bill-number { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 3px; }
                    .receipt-customer { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 4px; }
                    .datetime-center { text-align: center; font-size: 12px; font-weight: 700; padding: 2px 0; margin-bottom: 3px; }
                    .items-table { width: 100%; border-collapse: collapse; margin: 4px 0 6px 0; border: 2px solid #000; }
                    .items-table th { background: #e0e0e0; text-align: center; padding: 3px 3px; font-size: 9px; text-transform: uppercase; border: 1px solid #000; font-weight: 900; }
                    .items-table td { padding: 3px 3px; border: 1px solid #000; font-size: 10px; font-weight: 700; vertical-align: middle; text-align: center; }
                    .items-table th:first-child, .items-table td:first-child { text-align: center; }
                    .items-table th:nth-child(2), .items-table td:nth-child(2) { text-align: left; }
                    .product-name { font-weight: 900; word-break: break-word; }
                    .payment-box { margin-top: 4px; padding-top: 3px; border-top: 2px solid #000; }
                    .payment-row { display: flex; justify-content: space-between; padding: 1px 0; font-size: 12px; font-weight: 700; }
                    .payment-row.total { font-size: 14px; font-weight: 900; border-top: 2px solid #000; margin-top: 3px; padding-top: 4px; }
                    .footer { text-align: center; font-size: 10px; margin-top: 6px; padding-top: 4px; border-top: 2px solid #000; font-weight: 700; }
                    .thankyou { font-size: 12px; font-weight: 900; }
                    @media print {
                        body { margin: 0; padding: 3px; display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="receipt">
                    <div class="header-center">
                        ${businessLogo ? `<img src="${businessLogo}" alt="logo" class="header-logo" onerror="this.style.display='none'" />` : ''}
                        <div class="business-name">${escapeHtml(shopName)}</div>
                        ${businessPhone ? `<div class="business-phone">📞 ${escapeHtml(businessPhone)}</div>` : ''}
                        ${shopAddress ? `<div class="business-addr">${escapeHtml(shopAddress)}</div>` : ''}
                    </div>

                    <div class="receipt-title">${translations.purchaseReceipt || 'PURCHASE RECEIPT'}</div>
                    <div class="receipt-bill-number"><strong>${translations.billNumber || 'Bill No'}:</strong> #${escapeHtml(purchaseData.bill_no || '')}</div>
                    <div class="receipt-customer">${translations.supplier || 'Supplier'}: <strong>${escapeHtml(supplierName)}</strong></div>
                    <div class="datetime-center"><strong>${translations.date || 'Date'}:</strong> ${receiptDateTime}</div>

                    <table class="items-table">
                        <thead>
                            <tr>
                                <th style="width:8%;">#</th>
                                <th style="width:47%;">${translations.productName || 'Product'}</th>
                                <th style="width:15%;">${translations.quantity || 'Qty'}</th>
                                <th style="width:15%;">${translations.price || 'Price'}</th>
                                <th style="width:15%;">${translations.total || 'Total'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${purchaseData.items.map((item, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td class="text-left"><div class="product-name">${escapeHtml(item.product_name || 'Product')}</div></td>
                                    <td>${parseFloat(item.quantity).toFixed(0)}</td>
                                    <td>${parseFloat(item.price).toFixed(0)}</td>
                                    <td>${parseFloat(item.total || item.price * item.quantity).toFixed(0)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="payment-box">
                        <div class="payment-row"><span>${translations.subtotal || 'Subtotal'}</span><span>${formatReceiptCurrency(grandTotal)}</span></div>
                        ${parseFloat(tax) > 0 ? `<div class="payment-row"><span>${translations.tax || 'Tax'}</span><span>${formatReceiptCurrency(tax)}</span></div>` : ''}
                        ${parseFloat(discount) > 0 ? `<div class="payment-row"><span>${translations.discount || 'Discount'}</span><span>-${formatReceiptCurrency(discount)}</span></div>` : ''}
                        <div class="payment-row total"><span>${translations.grandTotal || 'GRAND TOTAL'}</span><span>${formatReceiptCurrency(grandTotal)}</span></div>
                        ${parseFloat(paidAmount) > 0 ? `<div class="payment-row"><span>${translations.paidAmount || 'Paid'}</span><span>${formatReceiptCurrency(paidAmount)}</span></div>` : ''}
                        ${parseFloat(balance) > 0 ? `<div class="payment-row"><span>${translations.balance || 'Balance'}</span><span>${formatReceiptCurrency(balance)}</span></div>` : ''}
                    </div>

                    <div class="footer">
                        <div class="thankyou">${translations.thankYou || 'Thank you for your business!'}</div>
                        <div>Developed by OurZest PVT LTD.</div>
                    </div>
                </div>

                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                            window.onafterprint = function() { window.close(); };
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
    };

    // ============ HANDLE VIEW ============
    const handleView = async (purchase) => {
        setSelectedPurchase(purchase);

        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedPurchase(data.data);
                }
            } catch (error) {
                console.error('Error fetching purchase details:', error);
            }
        }

        setShowViewModal(true);
    };

    // ============ HANDLE EDIT ============
    const handleEdit = (purchase) => {
        if (purchase.shop_id !== currentShop?.shop_id) {
            showToast('error', 'You can only edit purchases from the current shop');
            return;
        }
        setSelectedPurchase(purchase);
        setEditFormData({
            id: purchase.id,
            date: purchase.date ? purchase.date.split('T')[0] : '',
            bill_no: purchase.bill_no || '',
            supplierid: purchase.supplierid || '',
            supplier_name: purchase.supplier_name || '',
            phone: purchase.phone || '',
            address: purchase.address || '',
            grand_total: purchase.grand_total || '',
            paid: purchase.paid || '',
            balance: purchase.balance || '',
            items: purchase.items || []
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    // ============ HANDLE DELETE ============
    const handleDeleteClick = (purchaseId) => {
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase || purchase.shop_id !== currentShop?.shop_id) {
            showToast('error', 'You can only delete purchases from the current shop');
            return;
        }
        setPurchaseToDelete(purchaseId);
        setShowDeleteModal(true);
    };

    const handleUpdatePurchase = useCallback(async (e) => {
        e.preventDefault();

        const errors = {};
        if (!editFormData.date) errors.date = 'Date is required';
        if (!editFormData.supplier_name) errors.supplier_name = 'Supplier name is required';
        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            errors.grand_total = 'Valid grand total is required';
        }
        if (editFormData.paid === undefined || editFormData.paid === '') {
            errors.paid = 'Paid amount is required';
        }

        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit',
                    id: editFormData.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    supplierid: editFormData.supplierid,
                    date: editFormData.date,
                    bill_no: editFormData.bill_no,
                    supplier_name: editFormData.supplier_name,
                    phone: editFormData.phone || '',
                    address: editFormData.address || '',
                    grand_total: parseFloat(editFormData.grand_total) || 0,
                    paid: parseFloat(editFormData.paid) || 0,
                    balance: parseFloat(editFormData.balance) || 0,
                    items: editFormData.items
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.purchaseUpdated || 'Purchase updated successfully');
                setShowEditModal(false);
                fetchPurchases();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating purchase');
            }
        } catch (error) {
            console.error('Error updating purchase:', error);
            showToast('error', translations.updateError || 'Error updating purchase');
        } finally {
            setEditLoading(false);
        }
    }, [editFormData, user, currentShop, translations, showToast, fetchPurchases]);

    const handleDelete = async () => {
        if (!purchaseToDelete || !currentShop) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    id: purchaseToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Purchase deleted successfully');
                fetchPurchases();
                setShowDeleteModal(false);
                setPurchaseToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting purchase');
            }
        } catch (error) {
            console.error('Error deleting purchase:', error);
            showToast('error', translations.deleteError || 'Error deleting purchase');
        }
    };

    const handlePageChange = useCallback((page) => { 
        if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page);
    }, [pagination.totalPages]);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // ============ RENDER PURCHASE GROUP ============
    const renderPurchaseGroup = (purchase, index) => {
        const paymentStatus = getPaymentStatus(purchase.paid, purchase.grand_total);
        const addedBy = purchase.username || '-';
        const isSelfAdded = addedBy === user?.username;
        const items = purchase.items || [];
        const hasItems = items.length > 0;
        const supplierName = purchase.supplier_name || '-';

        return (
            <section className="ozlistui-sale-group" key={purchase.id}>
                <header className="ozlistui-sale-header">
                    <div className="ozlistui-sale-identity">
                        <div className="ozlistui-sale-invoice-icon" aria-hidden="true">
                            <FontAwesomeIcon icon={faFileInvoice} />
                        </div>
                        <div className="ozlistui-sale-identity-content">
                            <div className="ozlistui-sale-eyebrow-row">
                                <span className="ozlistui-sale-eyebrow">{translations.purchaseInvoice || 'Purchase invoice'}</span>
                                <span className="ozlistui-sale-header-date">
                                    <FontAwesomeIcon icon={faCalendar} /> {formatDate(purchase.date)}
                                </span>
                            </div>
                            <div className="ozlistui-sale-title-row">
                                <span className="ozlistui-sale-bill">#{purchase.bill_no || '-'}</span>
                                <span className="ozlistui-sale-title-separator" aria-hidden="true" />
                                <span className="ozlistui-sale-customer">
                                    <FontAwesomeIcon icon={faUser} /> {supplierName}
                                </span>
                            </div>
                            <div className="ozlistui-sale-status-row">
                                <span className={`ozlistui-sale-status ${paymentStatus.status}`}>
                                    <span className="ozlistui-sale-status-dot" aria-hidden="true" />
                                    {paymentStatus.text}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="ozlistui-sale-summary">
                        <div className="ozlistui-sale-metrics" aria-label={translations.paymentSummary || 'Payment summary'}>
                            <div className="ozlistui-sale-metric total"><span>{translations.grandTotal || 'Invoice total'}</span><strong>{formatCurrency(purchase.grand_total)}</strong></div>
                            <div className="ozlistui-sale-metric paid"><span>{translations.paid || 'Paid'}</span><strong>{formatCurrency(purchase.paid)}</strong></div>
                            <div className={`ozlistui-sale-metric balance ${parseFloat(purchase.balance) > 0 ? 'positive' : 'zero'}`}><span>{translations.balance || 'Amount due'}</span><strong>{formatCurrency(purchase.balance)}</strong></div>
                        </div>
                        <div className="ozlistui-sale-actions" aria-label={translations.actions || 'Invoice actions'}>
                            <button type="button" className="ozlistui-sale-action print" onClick={() => handlePrintBill(purchase)} title={translations.print || 'Print'}>
                                <FontAwesomeIcon icon={faPrint} />
                            </button>
                            <button type="button" className="ozlistui-sale-action view" onClick={() => handleView(purchase)} title={translations.view || 'View'}>
                                <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button type="button" className="ozlistui-sale-action edit" onClick={() => handleEdit(purchase)} title={translations.edit || 'Edit'} disabled={!isSelfAdded}>
                                <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button type="button" className="ozlistui-sale-action delete" onClick={() => handleDeleteClick(purchase.id)} title={translations.delete || 'Delete'} disabled={!isSelfAdded}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                </header>

                {hasItems && (
                    <div className="ozlistui-sale-products-section">
                    <div className="ozlistui-sale-products-wrap">
                        <table className="ozlistui-sale-products">
                            <thead>
                                <tr>
                                    <th className="ozlistui-sale-center">#</th>
                                    <th>{translations.productName || 'Product name'}</th>
                                    <th>{translations.barcode || 'Barcode'}</th>
                                    <th className="ozlistui-sale-number">{translations.costPrice || 'Cost price'}</th>
                                    <th className="ozlistui-sale-number">{translations.quantity || 'Quantity'}</th>
                                    <th className="ozlistui-sale-number">{translations.total || 'Total'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => {
                                    const itemTotal = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);

                                    return (
                                        <tr key={idx}>
                                            <td className="ozlistui-sale-center"><span className="ozlistui-sale-row-number">{idx + 1}</span></td>
                                            <td><span className="ozlistui-sale-product-name">{item.product_name || '-'}</span></td>
                                            <td><code className="ozlistui-sale-barcode">{item.barcode || '-'}</code></td>
                                            <td className="ozlistui-sale-number">{formatCurrency(item.price)}</td>
                                            <td className="ozlistui-sale-number"><strong>{parseFloat(item.quantity || 0).toFixed(0)}</strong></td>
                                            <td className="ozlistui-sale-number ozlistui-sale-line-total">{formatCurrency(itemTotal)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    </div>
                )}
            </section>
        );
    };

    // ============ MAIN RENDER ============
    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
                darkMode={darkMode}
            />

            {/* HEADER */}
            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{translations.purchaseList || 'Purchase List'}</h1>
                            <p>{translations.formDetails || 'All purchase details are below'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                        </button>
                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* SHOP INDICATOR */}
            {!currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view purchases'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* FILTERS */}
                    {showFilters && (
                        <div className="ozlistui-filters-section">
                            <div className="ozlistui-filters-row">
                                <CustomDropdown
                                    label={translations.filterBy || 'Filter By'}
                                    icon={faTag}
                                    value={filterBy}
                                    onChange={setFilterBy}
                                    options={filterByOptions}
                                    translations={translations}
                                />

                                {filterBy === 'billNumber' && (
                                    <div className="ozlistui-filter-group">
                                        <div className="ozlistui-floating-wrapper">
                                            <input type="text" id="billNumberFilter" value={billNumberFilter} onChange={(e) => setBillNumberFilter(e.target.value)} placeholder=" " />
                                            <label htmlFor="billNumberFilter"><FontAwesomeIcon icon={faTag} />{translations.billNumberFilter || 'Bill Number'}</label>
                                        </div>
                                    </div>
                                )}

                                {filterBy === 'supplierName' && (
                                    <div className="ozlistui-filter-group">
                                        <div className="ozlistui-floating-wrapper">
                                            <input type="text" id="supplierNameFilter" value={supplierNameFilter} onChange={(e) => setSupplierNameFilter(e.target.value)} placeholder=" " />
                                            <label htmlFor="supplierNameFilter"><FontAwesomeIcon icon={faUser} />{translations.supplierNameFilter || 'Supplier Name'}</label>
                                        </div>
                                    </div>
                                )}

                                {filterBy === 'phone' && (
                                    <div className="ozlistui-filter-group">
                                        <div className="ozlistui-floating-wrapper">
                                            <input type="tel" id="phoneFilter" value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} placeholder=" " dir="ltr" />
                                            <label htmlFor="phoneFilter"><FontAwesomeIcon icon={faPhone} />{translations.phoneFilter || 'Phone Number'}</label>
                                        </div>
                                    </div>
                                )}

                                {filterBy === 'added_by' && (
                                    <div className="ozlistui-filter-group">
                                        <div className="ozlistui-floating-wrapper">
                                            <input type="text" id="addedByFilter" value={addedByFilter} onChange={(e) => setAddedByFilter(e.target.value)} placeholder=" " />
                                            <label htmlFor="addedByFilter"><FontAwesomeIcon icon={faUser} />{translations.addedByFilter || 'Added By'}</label>
                                        </div>
                                    </div>
                                )}

                                {filterBy === 'date' && (
                                    <>
                                        <div className="ozlistui-filter-group ozlistui-date-filter">
                                            <div className="ozlistui-floating-wrapper">
                                                <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={getTodayDate()} placeholder=" " />
                                                <label htmlFor="startDate"><FontAwesomeIcon icon={faCalendar} />{translations.startDate || 'Start Date'}</label>
                                            </div>
                                        </div>
                                        <div className="ozlistui-filter-group ozlistui-date-filter">
                                            <div className="ozlistui-floating-wrapper">
                                                <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={getTodayDate()} placeholder=" " />
                                                <label htmlFor="endDate"><FontAwesomeIcon icon={faCalendar} />{translations.endDate || 'End Date'}</label>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {filterBy === 'paymentStatus' && (
                                    <CustomDropdown
                                        label={translations.paymentStatusFilter || 'Payment Status'}
                                        icon={faCreditCard}
                                        value={paymentStatusFilter}
                                        onChange={setPaymentStatusFilter}
                                        options={paymentStatusOptions}
                                        translations={translations}
                                    />
                                )}

                                <CustomDropdown
                                    label={translations.orientation || 'Orientation'}
                                    icon={faFilePdf}
                                    value={pdfOrientation}
                                    onChange={setPdfOrientation}
                                    options={pdfOrientationOptions}
                                    translations={translations}
                                />

                                <CustomDropdown
                                    label={translations.pageSize || 'Page Size'}
                                    icon={faFilePdf}
                                    value={pdfPageSize}
                                    onChange={setPdfPageSize}
                                    options={pdfPageSizeOptions}
                                    translations={translations}
                                />

                                <CustomDropdown
                                    label="Page Range"
                                    icon={faFilePdf}
                                    value={pdfPageRange}
                                    onChange={setPdfPageRange}
                                    options={pdfPageRangeOptions}
                                    translations={translations}
                                />

                                {pdfPageRange === 'range' && (
                                    <>
                                        <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                            <div className="ozlistui-floating-wrapper">
                                                <input type="number" min="1" value={pdfStartPage} onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))} placeholder=" " />
                                                <label>From</label>
                                            </div>
                                        </div>
                                        <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                            <div className="ozlistui-floating-wrapper">
                                                <input type="number" min={pdfStartPage} value={pdfEndPage} onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))} placeholder=" " />
                                                <label>To</label>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="ozlistui-filter-group">
                                    <button onClick={fetchPurchases} className="ozlistui-refresh-btn" disabled={loading}>
                                        <FontAwesomeIcon icon={faRefresh} />
                                        <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                                    </button>
                                </div>

                                <div className="ozlistui-filter-group">
                                    <button
                                        onClick={exportToPDF}
                                        className="ozlistui-refresh-btn"
                                        disabled={filteredPurchases.length === 0 || pdfLoading}
                                        style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                                    >
                                        {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                        <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PURCHASE GROUPS */}
                    {loading ? (
                        <div className="ozlistui-loading-container">
                            <div className="ozlistui-loading-spinner"></div>
                            <p>{translations.loadingUserData || 'Loading your purchase data...'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="ozlistui-table-scroll ozlistui-sales-ledger-scroll">
                                <div className="ozlistui-table-container ozlistui-sales-ledger">
                                {pagination.currentPurchases.map((purchase, index) => renderPurchaseGroup(purchase, index))}
                                </div>
                            </div>

                            {/* PAGINATION */}
                            {filteredPurchases.length > 0 && currentShop && (
                                <div className="ozlistui-pagination">
                                    <div className="ozlistui-pagination-left">
                                        <div className="ozlistui-per-page">
                                            <span>{translations.show || 'Show'}</span>
                                            <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="ozlistui-per-page-select">
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={500}>500</option>
                                                <option value={1000}>1000</option>
                                            </select>
                                            <span>{translations.perPage || 'per page'}</span>
                                        </div>
                                        <div className="ozlistui-pagination-info">
                                            <span>
                                                {translations.showing || 'Showing'}
                                                <strong> {pagination.startIndex + 1} </strong>
                                                {translations.to || 'to'}
                                                <strong> {Math.min(pagination.startIndex + itemsPerPage, filteredPurchases.length)} </strong>
                                                {translations.of || 'of'}
                                                <strong> {filteredPurchases.length} </strong>
                                                {translations.purchases || 'purchases'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ozlistui-pagination-right">
                                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                            <span>{translations.previous || 'Previous'}</span>
                                        </button>
                                        <div className="ozlistui-page-numbers">
                                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= pagination.totalPages - 2) {
                                                    pageNum = pagination.totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                                            <span>{translations.next || 'Next'}</span>
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* VIEW MODAL */}
            {showViewModal && selectedPurchase && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={() => setShowViewModal(false)}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faReceipt} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.purchaseDetails || 'Purchase Details'}</h3>
                                <span className="ozlistui-purchase-number-badge">#{selectedPurchase.bill_no}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faInfoCircle} />Purchase Information</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.purchaseId || 'Purchase ID'}:</span><span className="ozlistui-view-value">#{selectedPurchase.id}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.date || 'Date'}:</span><span className="ozlistui-view-value">{formatDate(selectedPurchase.date)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.billNumber || 'Bill Number'}:</span><span className="ozlistui-view-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedPurchase.bill_no}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.supplierName || 'Supplier Name'}:</span><span className="ozlistui-view-value">{selectedPurchase.supplier_name || '-'}</span></div>
                                    {selectedPurchase.phone && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.phone || 'Phone'}:</span><span className="ozlistui-view-value">{selectedPurchase.phone}</span></div>
                                    )}
                                    {selectedPurchase.address && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.supplierAddress || 'Address'}:</span><span className="ozlistui-view-value">{selectedPurchase.address}</span></div>
                                    )}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.addedBy || 'Added By'}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge">{selectedPurchase.username || '-'}</span></span></div>
                                </div>
                            </div>

                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faCalculator} />Payment Summary</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.subtotal || 'Subtotal'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedPurchase.subtotal)}</span></div>
                                    {parseFloat(selectedPurchase.tax) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.tax || 'Tax'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedPurchase.tax)}</span></div>}
                                    {parseFloat(selectedPurchase.discount) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.discount || 'Discount'}:</span><span className="ozlistui-view-value">-{formatCurrency(selectedPurchase.discount)}</span></div>}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.grandTotal || 'Grand Total'}:</span><span className="ozlistui-view-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.grand_total)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.paidAmount || 'Paid Amount'}:</span><span className="ozlistui-view-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.paid)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.balance || 'Balance'}:</span><span className="ozlistui-view-value" style={{ color: parseFloat(selectedPurchase.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.balance)}</span></div>
                                </div>
                            </div>

                            {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBoxes} />{translations.items || 'Items'}</h4>
                                    <div className="ozlistui-items-table-container">
                                        <div className="ozlistui-items-table-wrapper">
                                            <table className="ozlistui-items-table">
                                                <thead>
                                                    <tr>
                                                        <th>{translations.srNo || 'Sr#'}</th>
                                                        <th>{translations.batch || 'Batch'}</th>
                                                        <th>{translations.productName || 'Product'}</th>
                                                        <th>{translations.category || 'Category'}</th>
                                                        <th>{translations.barcode || 'Barcode'}</th>
                                                        <th className="ozlistui-text-right">{translations.costPrice || 'Cost Price'}</th>
                                                        <th className="ozlistui-text-right">{translations.sellPrice || 'Sell Price'}</th>
                                                        <th className="ozlistui-text-right">{translations.quantity || 'Qty'}</th>
                                                        <th className="ozlistui-text-right">{translations.stock || 'Stock'}</th>
                                                        <th className="ozlistui-text-right">{translations.total || 'Total'}</th>
                                                        <th>{translations.expiry || 'Expiry'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedPurchase.items.map((item, idx) => {
                                                        const itemTotal = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
                                                        const stockPacks = parseFloat(item.available_stock_packs || item.quantity || 0);
                                                        const stockUnits = parseFloat(item.bquantity ? parseBQuantityNumeric(item.bquantity) : 0);
                                                        const isPackingMode = (item.purchase_type || 'packing') === 'packing';
                                                        const stockDisplay = isPackingMode ? stockPacks.toFixed(0) : stockUnits.toFixed(2);

                                                        return (
                                                            <tr key={idx}>
                                                                <td>{idx + 1}</td>
                                                                <td>{item.batch_no || '-'}</td>
                                                                <td><strong>{item.product_name || '-'}</strong></td>
                                                                <td>{item.category || '-'}</td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.barcode || '-'}</td>
                                                                <td className="ozlistui-text-right">{formatCurrency(item.price)}</td>
                                                                <td className="ozlistui-text-right">{item.sell_per_pack ? formatCurrency(item.sell_per_pack) : '-'}</td>
                                                                <td className="ozlistui-text-right">{parseFloat(item.quantity).toFixed(2)}</td>
                                                                <td className="ozlistui-text-right">{stockDisplay}</td>
                                                                <td className="ozlistui-text-right ozlistui-total-cell">{formatCurrency(itemTotal)}</td>
                                                                <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handlePrintBill(selectedPurchase); }}>
                                <FontAwesomeIcon icon={faPrint} />
                                {translations.print || 'Print'}
                            </button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedPurchase); }}>
                                <FontAwesomeIcon icon={faEdit} />
                                {translations.edit || 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {showEditModal && selectedPurchase && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div
                        ref={modalRef}
                        className="ozlistui-modal-content ozlistui-edit-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.editPurchase || 'Edit Purchase'}</h3>
                                <span className="ozlistui-purchase-number-badge">#{selectedPurchase.bill_no}</span>
                            </div>
                            <div className="ozlistui-width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdatePurchase}>
                            <div className="ozlistui-modal-body">
                                <div className="ozlistui-form-grid">
                                    <div className={`ozlistui-form-group ${editFormErrors.date ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input type="date" id="edit_date" name="date" value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} max={getTodayDate()} placeholder=" " className={editFormErrors.date ? 'ozlistui-error' : ''} />
                                            <label htmlFor="edit_date"><FontAwesomeIcon icon={faCalendar} />{translations.date || 'Date'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                        {editFormErrors.date && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.date}</div>}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.bill_no ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input type="text" id="edit_bill_no" name="bill_no" value={editFormData.bill_no} onChange={(e) => setEditFormData({ ...editFormData, bill_no: e.target.value })} placeholder=" " />
                                            <label htmlFor="edit_bill_no"><FontAwesomeIcon icon={faFileInvoice} />{translations.billNumber || 'Bill Number'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                        {editFormErrors.bill_no && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.bill_no}</div>}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.supplier_name ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input type="text" id="edit_supplier_name" name="supplier_name" value={editFormData.supplier_name} onChange={(e) => setEditFormData({ ...editFormData, supplier_name: e.target.value })} placeholder=" " />
                                            <label htmlFor="edit_supplier_name"><FontAwesomeIcon icon={faUser} />{translations.supplierName || 'Supplier Name'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                        {editFormErrors.supplier_name && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.supplier_name}</div>}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="tel" id="edit_phone" name="phone" value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} placeholder=" " dir="ltr" />
                                            <label htmlFor="edit_phone"><FontAwesomeIcon icon={faPhone} />{translations.phone || 'Phone Number'} <span>({translations.optional || 'Optional'})</span></label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea id="edit_address" name="address" value={editFormData.address} onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })} rows="2" placeholder=" "></textarea>
                                            <label htmlFor="edit_address"><FontAwesomeIcon icon={faMapMarkerAlt} />{translations.supplierAddress || 'Address'} <span>({translations.optional || 'Optional'})</span></label>
                                        </div>
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.grand_total ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input type="number" id="edit_grand_total" name="grand_total" value={editFormData.grand_total} onChange={(e) => {
                                                const grandTotal = e.target.value;
                                                setEditFormData(prev => {
                                                    const paid = parseFloat(prev.paid) || 0;
                                                    const balance = parseFloat(grandTotal) - paid;
                                                    return { ...prev, grand_total: grandTotal, balance: balance.toFixed(2) };
                                                });
                                            }} step="0.01" min="0" placeholder="0.00" />
                                            <label htmlFor="edit_grand_total"><FontAwesomeIcon icon={faDollarSign} />{translations.grandTotal || 'Grand Total'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                        {editFormErrors.grand_total && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.grand_total}</div>}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.paid ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input type="number" id="edit_paid" name="paid" value={editFormData.paid} onChange={(e) => {
                                                const paid = e.target.value;
                                                setEditFormData(prev => {
                                                    const grandTotal = parseFloat(prev.grand_total) || 0;
                                                    const balance = grandTotal - parseFloat(paid);
                                                    return { ...prev, paid: paid, balance: balance.toFixed(2) };
                                                });
                                            }} step="0.01" min="0" placeholder="0.00" />
                                            <label htmlFor="edit_paid"><FontAwesomeIcon icon={faMoneyBill} />{translations.paidAmount || 'Paid Amount'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                        {editFormErrors.paid && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.paid}</div>}
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-balance-display">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="text" id="edit_balance" value={formatCurrency(editFormData.balance)} readOnly className="ozlistui-readonly-input" style={{ color: parseFloat(editFormData.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }} />
                                            <label htmlFor="edit_balance"><FontAwesomeIcon icon={faBalanceScale} />{translations.balance || 'Balance'}</label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-edit-info-note">
                                        <p><FontAwesomeIcon icon={faInfoCircle} /> {translations.readonlyNotice || 'Bill number and items cannot be edited after creation'}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />{translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                    {editLoading ? (
                                        <><div className="ozlistui-btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>
                                    ) : (
                                        <><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DELETE MODAL */}
            {showDeleteModal && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{translations.deleteMessage || 'Are you sure you want to delete this purchase?'}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CosmeticsInvoicePurchaseList;
